import React, { useState } from 'react';
import { LuUserCheck } from 'react-icons/lu';
import {
    IoCalendarOutline,
    IoTimeOutline,
    IoChevronDown,
    IoCloseOutline
} from 'react-icons/io5';
import { FiPlus, FiSave } from 'react-icons/fi';

export default function CreateStaffInjuryModal({ onClose }) {
    const [formData, setFormData] = useState({
        location: '',
        date: '2025-05-28',
        time: '11:05',
        staffMember: '',
        description: '',
        injuryType: '',
        bodyPart: '',
        immediateAction: '',
    });

    const [images, setImages] = useState([
        'https://unsplash.com' // Hand bandage placeholder
    ]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        console.log('Submitting Staff Incident:', formData);
        onClose();
    };

    return (
        <div className="w-full bg-white font-sans rounded-xl text-slate-800">
            {/* Header section */}
            <div className="p-5 pb-4 border-b border-slate-100 flex justify-between items-start">
                <div className="flex items-center gap-3">
                    <div className="p-2.5 bg-amber-50 text-amber-500 rounded-xl border border-amber-100">
                        <LuUserCheck className="text-xl" />
                    </div>
                    <div>
                        <h2 className="text-lg font-bold tracking-tight">Create Staff Injury</h2>
                        <p className="text-xs text-slate-400 mt-0.5">Record details of injury or accident involving a staff member.</p>
                    </div>
                </div>
                <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors p-1">
                    <IoCloseOutline className="text-2xl" />
                </button>
            </div>

            {/* Form Body */}
            <form onSubmit={handleSubmit} className="p-5 space-y-4 max-h-[75vh] overflow-y-auto">

                {/* Row: Location & Date/Time */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {/* Location */}
                    <div className="flex flex-col gap-1.5">
                        <label className="text-xs font-bold text-slate-700">
                            Location <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                            <select
                                name="location"
                                value={formData.location}
                                onChange={handleChange}
                                required
                                className="w-full text-sm bg-white border border-slate-200 rounded-xl px-3 py-2.5 appearance-none focus:outline-none focus:border-blue-500 text-slate-600"
                            >
                                <option value="">Select location</option>
                                <option value="Kitchen Main">Kitchen Main</option>
                                <option value="Prep Station">Prep Station</option>
                                <option value="Dining Floor">Dining Floor</option>
                                <option value="Storage Room">Storage Room</option>
                            </select>
                            <IoChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                        </div>
                    </div>

                    {/* Date & Time */}
                    <div className="flex flex-col gap-1.5">
                        <label className="text-xs font-bold text-slate-700">
                            Date & Time <span className="text-red-500">*</span>
                        </label>
                        <div className="flex gap-2">
                            <div className="relative flex-1">
                                <input
                                    type="date"
                                    name="date"
                                    value={formData.date}
                                    onChange={handleChange}
                                    required
                                    className="w-full text-sm border border-slate-200 rounded-xl pl-3 pr-8 py-2.5 focus:outline-none focus:border-blue-500"
                                />
                                <IoCalendarOutline className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none text-base" />
                            </div>
                            <div className="relative w-28">
                                <input
                                    type="text"
                                    name="time"
                                    value={formData.time}
                                    onChange={handleChange}
                                    required
                                    className="w-full text-sm border border-slate-200 rounded-xl pl-3 pr-7 py-2.5 focus:outline-none focus:border-blue-500"
                                />
                                <IoTimeOutline className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none text-base" />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Staff Member Select */}
                <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-bold text-slate-700">
                        Staff Member <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                        <select
                            name="staffMember"
                            value={formData.staffMember}
                            onChange={handleChange}
                            required
                            className="w-full text-sm bg-white border border-slate-200 rounded-xl px-3 py-2.5 appearance-none focus:outline-none focus:border-blue-500"
                        >
                            <option value="">Select staff member</option>
                            <option value="John Doe (Chef)">John Doe (Chef)</option>
                            <option value="Jane Smith (Server)">Jane Smith (Server)</option>
                            <option value="Mike Myers (Line Cook)">Mike Myers (Line Cook)</option>
                        </select>
                        <IoChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                    </div>
                </div>

                {/* Description */}
                <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-bold text-slate-700">
                        Description <span className="text-red-500">*</span>
                    </label>
                    <textarea
                        name="description"
                        rows="3"
                        value={formData.description}
                        onChange={handleChange}
                        required
                        placeholder="Describe what happened, how the injury occurred and type of injury."
                        className="w-full text-sm border border-slate-200 rounded-xl p-3 placeholder:text-slate-400 focus:outline-none focus:border-blue-500 resize-none leading-relaxed"
                    ></textarea>
                </div>

                {/* Injury Type */}
                <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-bold text-slate-700">
                        Injury Type <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                        <select
                            name="injuryType"
                            value={formData.injuryType}
                            onChange={handleChange}
                            required
                            className="w-full text-sm bg-white border border-slate-200 rounded-xl px-3 py-2.5 appearance-none focus:outline-none focus:border-blue-500"
                        >
                            <option value="">Select injury type</option>
                            <option value="Cut / Laceration">Cut / Laceration</option>
                            <option value="Burn / Scald">Burn / Scald</option>
                            <option value="Strain / Sprain">Strain / Sprain</option>
                            <option value="Slip / Trip">Slip / Trip</option>
                        </select>
                        <IoChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                    </div>
                </div>

                {/* Body Part Affected */}
                <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-bold text-slate-700">Body Part Affected</label>
                    <div className="relative">
                        <select
                            name="bodyPart"
                            value={formData.bodyPart}
                            onChange={handleChange}
                            className="w-full text-sm bg-white border border-slate-200 rounded-xl px-3 py-2.5 appearance-none focus:outline-none focus:border-blue-500"
                        >
                            <option value="">Select body part</option>
                            <option value="Hand / Fingers">Hand / Fingers</option>
                            <option value="Arm / Wrist">Arm / Wrist</option>
                            <option value="Foot / Ankle">Foot / Ankle</option>
                            <option value="Head / Face">Head / Face</option>
                        </select>
                        <IoChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                    </div>
                </div>

                {/* Immediate Action Taken */}
                <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-bold text-slate-700">Immediate Action Taken</label>
                    <textarea
                        name="immediateAction"
                        rows="2"
                        value={formData.immediateAction}
                        onChange={handleChange}
                        placeholder="What action was taken immediately?"
                        className="w-full text-sm border border-slate-200 rounded-xl p-3 placeholder:text-slate-400 focus:outline-none focus:border-blue-500 resize-none leading-relaxed"
                    ></textarea>
                </div>

                {/* Photos / Evidence */}
                <div className="flex flex-col gap-1">
                    <label className="text-xs font-bold text-slate-700">Photos / Evidence</label>
                    <p className="text-[11px] text-slate-400">Add photos if available</p>

                    <div className="flex flex-wrap gap-3 mt-2">
                        {images.map((src, i) => (
                            <div key={i} className="w-24 h-24 rounded-xl overflow-hidden border border-slate-100 shadow-sm relative">
                                <img src={src} alt="Evidence" className="w-full h-full object-cover" />
                            </div>
                        ))}
                        <button
                            type="button"
                            className="w-24 h-24 border border-dashed border-slate-300 rounded-xl flex flex-col items-center justify-center gap-1 text-slate-400 hover:text-slate-600 hover:border-slate-400 transition-colors bg-slate-50/50"
                        >
                            <FiPlus className="text-lg" />
                            <span className="text-[11px] font-medium">Add more</span>
                        </button>
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center gap-3 pt-4 border-t border-slate-100">
                    <button
                        type="button"
                        onClick={onClose}
                        className="flex-1 py-2.5 border border-slate-200 rounded-xl text-sm font-semibold hover:bg-slate-50 transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className="flex-1 py-2.5 bg-amber-500 hover:bg-amber-600 text-white rounded-xl text-sm font-semibold flex items-center justify-center gap-2 transition-colors shadow-sm"
                    >
                        <FiSave className="text-base" />
                        Save Incident
                    </button>
                </div>

            </form>
        </div>
    );
}
